'use client';

import React, { useCallback, useMemo, useState } from 'react';
import PackagesFilter from '../PackagesFilter';
import Packages from '../Packages';
import { FormSubmissionModal } from '@/components/forms/FormSubmissionModal/FormSubmissionModal';
import {
  serviceEnquiryDefaultValues,
  serviceEnquiryFields,
  serviceEnquirySchema,
} from '@/features/enquiries/forms/serviceEnquirySchema';
import {
  FlatCategoryItem,
  FlatPackagesClientWrapperProps,
  FilterState,
  SelectOption,
  EnquirePayload,
} from '../../types';

// ---- helpers ----
const normalizeGender = (val: unknown): string | null => {
  const s = String(val || '')
    .toLowerCase()
    .trim();
  if (!s) return null;
  if (/\b(both|all|any|unisex)\b/.test(s)) return 'both';
  if (/\b(female|women|woman|ladies|girl|girls)\b/.test(s)) return 'female';
  if (/\b(male|men|man|gent|boy|boys)\b/.test(s)) return 'male';
  return null;
};

const parsePrice = (val: unknown): number | null => {
  if (!val) return null;
  const m = String(val)
    .replace(/,/g, '')
    // eslint-disable-next-line security/detect-unsafe-regex -- False positive: \d+ and \.\d+ match disjoint character classes, no exponential backtracking
    .match(/(\d+(\.\d+)?)/);
  return m ? Number(m[1]) : null;
};

const inPriceRange = (price: number | null, range: string): boolean => {
  if (price === null) return true;
  switch (range) {
    case 'under-500':
      return price < 500;
    case '500-1000':
      return price >= 500 && price <= 1000;
    case '1000-2000':
      return price > 1000 && price <= 2000;
    case '2000-plus':
      return price > 2000;
    default:
      return true;
  }
};
// -----------------

const GENDER_OPTIONS: SelectOption[] = [
  { label: 'All Genders', value: 'all' },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

const PRICE_RANGE_OPTIONS: SelectOption[] = [
  { label: 'All Prices', value: 'all' },
  { label: 'Under £500', value: 'under-500' },
  { label: '£500–£1000', value: '500-1000' },
  { label: '£1000–£2000', value: '1000-2000' },
  { label: '£2000+', value: '2000-plus' },
];

const EMPTY_FILTER: FilterState = {
  category: '',
  gender: '',
  priceRange: '',
  query: '',
};

const PackagesClientWrapper = ({
  data,
  onEnquire: externalOnEnquire,
  onBook: externalOnBook,
}: FlatPackagesClientWrapperProps): React.ReactElement => {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER);

  const {
    categories: rawCategories,
    heading,
    description,
    labels,
  } = data ?? {};
  const items = useMemo(() => rawCategories ?? [], [rawCategories]);

  const categoryOptions = useMemo((): SelectOption[] => {
    const opts = items
      .filter((c) => Boolean(c?.title && c?.slug))
      .map((c) => ({ label: c.title, value: c.slug }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: 'All Categories', value: 'all' }, ...opts];
  }, [items]);

  const filteredCategories = useMemo((): FlatCategoryItem[] => {
    const q = filters.query.trim().toLowerCase();
    const wantGender =
      filters.gender !== 'all' ? filters.gender.toLowerCase() : '';

    return items
      .map((cat) => {
        let pkgs = cat.packages ?? [];

        if (q) {
          pkgs = pkgs.filter((pkg) =>
            (pkg.title || '').toLowerCase().includes(q),
          );
        }

        if (wantGender) {
          pkgs = pkgs.filter((pkg) => {
            const g = normalizeGender(pkg.gender);
            if (!g) return false;
            return g === 'both' || g === wantGender;
          });
        }

        const effectivePriceRange =
          filters.priceRange !== 'all' ? filters.priceRange : '';
        pkgs = pkgs.filter((pkg) =>
          inPriceRange(parsePrice(pkg.price), effectivePriceRange),
        );

        return { ...cat, packages: pkgs };
      })
      .filter((cat) => {
        const count = cat.packages?.length || 0;
        if (!q) return count > 0;
        return (cat.title || '').toLowerCase().includes(q) || count > 0;
      })
      .filter((cat) => {
        const active = filters.category && filters.category !== 'all';
        return active
          ? cat.slug.toLowerCase() === filters.category.toLowerCase()
          : true;
      });
  }, [items, filters]);

  const totalAfterFilter = useMemo(
    () =>
      filteredCategories.reduce((sum, c) => sum + (c.packages?.length || 0), 0),
    [filteredCategories],
  );

  const resetFilters = useCallback((): void => setFilters(EMPTY_FILTER), []);

  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [enquirePayload, setEnquirePayload] = useState<EnquirePayload>({
    category: '',
  });

  const handleEnquire = useCallback(
    (payload: EnquirePayload) => {
      if (externalOnEnquire) {
        externalOnEnquire(payload);
        return;
      }

      setEnquirePayload(payload);
      setHasBeenOpened(true);
      setEnquiryOpen(true);
    },
    [externalOnEnquire],
  );

  return (
    <>
      <PackagesFilter
        heading={heading}
        description={description}
        categories={categoryOptions}
        genders={GENDER_OPTIONS}
        priceRanges={PRICE_RANGE_OPTIONS}
        value={filters}
        onChange={setFilters}
        onClear={resetFilters}
      />

      {totalAfterFilter === 0 ? (
        <div className="mx-auto my-10 max-w-2xl rounded-xl border border-gray-200 p-6 text-center">
          <p className="text-lg font-medium">No packages match your filters.</p>
          <p className="mt-2 text-sm text-gray-600">
            Try changing the category, gender, price range, or search term.
          </p>
          <button
            onClick={resetFilters}
            className="mt-4 cursor-pointer rounded-lg bg-black px-4 py-2 text-white"
          >
            Reset filters
          </button>
        </div>
      ) : (
        <Packages
          data={filteredCategories}
          labels={labels}
          onEnquire={handleEnquire}
          onBook={externalOnBook}
        />
      )}

      {!externalOnEnquire && hasBeenOpened && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId="serviceEnquiry"
          schema={serviceEnquirySchema}
          fields={serviceEnquiryFields}
          defaultValues={serviceEnquiryDefaultValues}
          title="Enquire about this health package"
          description={`You are enquiring about: ${enquirePayload.title || enquirePayload.category}`}
          submitLabel="Send enquiry"
          metadata={{
            serviceName: enquirePayload.title || enquirePayload.category,
            pageType: 'health-package',
            source: 'health-packages-page',
          }}
        />
      )}
    </>
  );
};

export default PackagesClientWrapper;
